import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Card, Group, Loader, Stack, Switch, Text, TextInput, Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconDeviceFloppy, IconShieldLock, IconTool,
} from '@tabler/icons-react';
import { useResourceList, useUpdateResource } from '@/shared/api/jsonapi/hooks';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

export const Route = createFileRoute('/_app/admin/system')({ component: AdminSystemPage });

interface SetupRow extends Denormalized {
  type: 'setups';
  id: string;
  environment?: string;
  minAppVersion?: string;
  maintenance?: boolean;
  useRemoteFiles?: boolean;
  disableLog?: boolean;
  publicRegistration?: boolean;
}

type Patchable = Pick<SetupRow, 'minAppVersion' | 'maintenance' | 'useRemoteFiles' | 'disableLog' | 'publicRegistration'>;

function AdminSystemPage() {
  const { data, isLoading, isError } = useResourceList<SetupRow>('setups', { page: { size: 10 } });
  const update = useUpdateResource<SetupRow>('setups');

  // Prefer environment=web; fallback to first row.
  const setup = useMemo<SetupRow | undefined>(
    () => data?.data.find((s) => s.environment === 'web') ?? data?.data[0],
    [data],
  );

  const [draft, setDraft] = useState<Patchable>({});
  useEffect(() => {
    if (setup) {
      setDraft({
        minAppVersion: setup.minAppVersion ?? '',
        maintenance: !!setup.maintenance,
        useRemoteFiles: !!setup.useRemoteFiles,
        disableLog: !!setup.disableLog,
        publicRegistration: !!setup.publicRegistration,
      });
    }
  }, [setup]);

  const dirty = useMemo(() => {
    if (!setup) return false;
    return (draft.minAppVersion ?? '') !== (setup.minAppVersion ?? '')
      || !!draft.maintenance !== !!setup.maintenance
      || !!draft.useRemoteFiles !== !!setup.useRemoteFiles
      || !!draft.disableLog !== !!setup.disableLog
      || !!draft.publicRegistration !== !!setup.publicRegistration;
  }, [draft, setup]);

  const save = async () => {
    if (!setup) return;
    try {
      await update.mutateAsync({
        id: setup.id,
        attributes: {
          minAppVersion: (draft.minAppVersion ?? '').trim(),
          maintenance: !!draft.maintenance,
          useRemoteFiles: !!draft.useRemoteFiles,
          disableLog: !!draft.disableLog,
          publicRegistration: !!draft.publicRegistration,
        },
      });
      notifications.show({ color: 'green', message: 'Setup aggiornato' });
    } catch (e) {
      notifications.show({ color: 'red', message: (e as Error).message || 'Errore' });
    }
  };

  return (
    <Stack maw={820}>
      <div>
        <Title order={1}>
          <IconTool size={26} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Sistema
        </Title>
        <Text c="dimmed" size="sm">
          Flag globali sul Setup. Le modifiche entrano in vigore immediatamente per tutti i tenant.
        </Text>
      </div>

      {isLoading && (
        <Card withBorder padding="lg" radius="md"><Group><Loader size="sm" /><Text>Carico setup…</Text></Group></Card>
      )}

      {isError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          Errore caricamento Setup.
        </Alert>
      )}

      {!isLoading && !isError && !setup && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
          Nessun record Setup trovato. Crealo dal database o tramite seed.
        </Alert>
      )}

      {setup && (
        <>
          <Card withBorder padding="lg" radius="md">
            <Stack>
              <Group justify="space-between">
                <div>
                  <Text fw={600}>Modalita&apos; manutenzione</Text>
                  <Text size="xs" c="dimmed">Blocca l&apos;accesso all&apos;applicazione (eccetto super-admin).</Text>
                </div>
                <Switch
                  size="xl"
                  onLabel="ON"
                  offLabel="OFF"
                  color="red"
                  checked={!!draft.maintenance}
                  onChange={(e) => setDraft((p) => ({ ...p, maintenance: e.currentTarget.checked }))}
                />
              </Group>
              {draft.maintenance && (
                <Alert color="red" icon={<IconShieldLock size={16} />}>
                  Attenzione: tutti gli utenti non super-admin verranno disconnessi al prossimo refresh.
                </Alert>
              )}
            </Stack>
          </Card>

          <Card withBorder padding="lg" radius="md">
            <Stack>
              <Text fw={600}>Altri flag</Text>

              <Group justify="space-between">
                <div>
                  <Text size="sm">Usa file remoti</Text>
                  <Text size="xs" c="dimmed">Serve assets statici da CDN remota invece che dal server locale.</Text>
                </div>
                <Switch
                  checked={!!draft.useRemoteFiles}
                  onChange={(e) => setDraft((p) => ({ ...p, useRemoteFiles: e.currentTarget.checked }))}
                />
              </Group>

              <Group justify="space-between">
                <div>
                  <Text size="sm">Disabilita log</Text>
                  <Text size="xs" c="dimmed">Spegne l&apos;invio dei log su MongoDB / log bus.</Text>
                </div>
                <Switch
                  color="orange"
                  checked={!!draft.disableLog}
                  onChange={(e) => setDraft((p) => ({ ...p, disableLog: e.currentTarget.checked }))}
                />
              </Group>

              <Group justify="space-between">
                <div>
                  <Text size="sm">Registrazione pubblica</Text>
                  <Text size="xs" c="dimmed">Consenti la self-registration di nuovi utenti.</Text>
                </div>
                <Switch
                  checked={!!draft.publicRegistration}
                  onChange={(e) => setDraft((p) => ({ ...p, publicRegistration: e.currentTarget.checked }))}
                />
              </Group>

              <TextInput
                label="Versione minima app"
                description="Le app con versione inferiore vengono forzate all'aggiornamento."
                placeholder="es. 1.5.0"
                value={draft.minAppVersion ?? ''}
                onChange={(e) => setDraft((p) => ({ ...p, minAppVersion: e.currentTarget.value }))}
              />
            </Stack>
          </Card>

          <Group justify="space-between">
            <Badge variant="light" color="gray">
              Setup id <code>{setup.id}</code>{setup.environment ? ` · env=${setup.environment}` : ''}
            </Badge>
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={() => void save()}
              disabled={!dirty || update.isPending}
              loading={update.isPending}
            >
              Salva
            </Button>
          </Group>
        </>
      )}
    </Stack>
  );
}
